import { fmt } from '@cca/ui';

// Dependency-free horizontal bar charts for the quota schedule + scenarios.
// Plain divs sized as a % of the largest value across all rows/series, so the
// scale is shared and rows compare directly. No chart library — keeps the bundle
// small (House Rules) and themes via the same CSS vars the tables use.

export interface ChartSeries {
  label: string;
  color: string;
}

export interface ChartRow {
  name: string;
  /** One value per series (same order as `series`); null renders an em-dash. */
  values: (number | null)[];
}

export function HBarChart({
  title,
  note,
  series,
  rows,
  format = (n) => fmt(n),
  maxHeight = 380,
}: {
  title: string;
  note?: string;
  series: ChartSeries[];
  rows: ChartRow[];
  format?: (n: number) => string;
  maxHeight?: number;
}) {
  if (rows.length === 0) return null;
  const max = Math.max(
    1,
    ...rows.flatMap((r) => r.values.map((v) => (v == null ? 0 : Math.abs(v)))),
  );
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: note ? 2 : 8 }}>{title}</div>
      {note && <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 8 }}>{note}</div>}
      {series.length > 1 && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 8 }}>
          {series.map((s) => (
            <span
              key={s.label}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11,
                color: 'var(--text2)',
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: s.color,
                  display: 'inline-block',
                }}
              />
              {s.label}
            </span>
          ))}
        </div>
      )}
      <div style={{ maxHeight, overflowY: 'auto', paddingRight: 4 }}>
        {rows.map((r) => (
          <div
            key={r.name}
            style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}
          >
            <div
              style={{
                width: 160,
                flexShrink: 0,
                fontSize: 11,
                textAlign: 'right',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={r.name}
            >
              {r.name}
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {r.values.map((v, i) => {
                const color = series[i]?.color ?? 'var(--text2)';
                const pct = v == null ? 0 : (Math.abs(v) / max) * 100;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div
                      style={{
                        flex: 1,
                        background: 'var(--bg2, #f0ece4)',
                        borderRadius: 3,
                        height: 12,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${pct}%`,
                          background: color,
                          height: '100%',
                          borderRadius: 3,
                          minWidth: v ? 2 : 0,
                        }}
                      />
                    </div>
                    <div
                      className="num"
                      style={{
                        width: 80,
                        flexShrink: 0,
                        fontSize: 11,
                        textAlign: 'right',
                        color: 'var(--text2)',
                      }}
                    >
                      {v == null ? '—' : format(v)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const pctOf = (v: number, total: number): number => (total > 0 ? (v / total) * 100 : 0);
export const asPct = (n: number): string => `${n.toFixed(1)}%`;
export { pctOf };

// Distinct-ish palette for pie slices; "Others" always renders grey.
const PIE_PALETTE = [
  '#2f6f4f',
  '#8a5a2b',
  '#3a5a8a',
  '#a23b3b',
  '#6b4f9a',
  '#2b8a8a',
  '#9a8a2b',
  '#c2693b',
  '#4f9a5a',
  '#8a3b6b',
];

export interface PieSlice {
  name: string;
  value: number;
}

// Proportion-of-total pie. With many churches a 40-slice pie is unreadable, so we
// show the top `topN` and fold the rest into a single grey "Others" wedge. Legend
// carries the exact % + amount. Dependency-free SVG.
export function PieChart({
  title,
  note,
  slices,
  topN = 10,
  size = 200,
}: {
  title: string;
  note?: string;
  slices: PieSlice[];
  topN?: number;
  size?: number;
}) {
  const positive = slices.filter((s) => s.value > 0).sort((a, b) => b.value - a.value);
  const total = positive.reduce((s, x) => s + x.value, 0);
  if (total <= 0) return null;

  const top = positive.slice(0, topN);
  const restVal = positive.slice(topN).reduce((s, x) => s + x.value, 0);
  const display: PieSlice[] = restVal > 0 ? [...top, { name: 'Others', value: restVal }] : top;

  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 2;
  let angle = -Math.PI / 2; // start at 12 o'clock
  const arcs = display.map((s, i) => {
    const frac = s.value / total;
    const a0 = angle;
    const a1 = angle + frac * 2 * Math.PI;
    angle = a1;
    const color = s.name === 'Others' ? '#9a9a9a' : (PIE_PALETTE[i % PIE_PALETTE.length] as string);
    // A single 100% slice can't be drawn as a wedge — render the full circle.
    if (frac >= 0.9999) {
      return { kind: 'circle' as const, color, name: s.name, value: s.value, frac };
    }
    const x0 = (cx + r * Math.cos(a0)).toFixed(2);
    const y0 = (cy + r * Math.sin(a0)).toFixed(2);
    const x1 = (cx + r * Math.cos(a1)).toFixed(2);
    const y1 = (cy + r * Math.sin(a1)).toFixed(2);
    const large = frac > 0.5 ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`;
    return { kind: 'path' as const, d, color, name: s.name, value: s.value, frac };
  });

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: note ? 2 : 8 }}>{title}</div>
      {note && <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 8 }}>{note}</div>}
      <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
          {arcs.map((a, i) =>
            a.kind === 'circle' ? (
              <circle key={i} cx={cx} cy={cy} r={r} fill={a.color} />
            ) : (
              <path key={i} d={a.d} fill={a.color} stroke="var(--bg, #fff)" strokeWidth={1} />
            ),
          )}
        </svg>
        <div style={{ minWidth: 200, maxHeight: size, overflowY: 'auto' }}>
          {arcs.map((a, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                padding: '1px 0',
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: a.color,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={a.name}
              >
                {a.name}
              </span>
              <span className="num" style={{ color: 'var(--text2)' }}>
                {asPct(a.frac * 100)} ({fmt(a.value)})
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
